/* Il risultato di un backtest: l'adattatore fra una riga del magazzino e il
 * componente condiviso.
 *
 * Il componente vero vive in `componenti/RisultatoOperazioni.tsx` perché lo
 * monteranno anche i tradebot — è da lì che nasce il confronto fra i segnali
 * veri di un bot e quelli della prova da cui è nato. Qui restano le due cose
 * che sono SOLO dei backtest: la traduzione della riga in serie normalizzate e
 * lo stato vuoto, che per un backtest ha una causa precisa e una cura precisa.
 */

import {
  Avvertenza,
  Distintivo,
  Pulsante,
  RisultatoOperazioni,
  TernaMetriche,
} from '../../componenti';
import { num } from '../../lib/formato';
import { serieDaBacktest } from '../../lib/operazioni';
import type { Backtest } from '../../api/tipi';
import './backtest.css';

export interface PropsRisultatoBacktest {
  dato: Backtest;
  /** Etichetta del periodo, es. «2017–2026»: viaggia con il risultato. */
  periodo: string;
  /** Rilancia questa stessa configurazione con `force`. Senza, il riquadro
   *  vuoto spiega comunque perché le serie mancano, ma non offre la cura. */
  onRiesegui?: () => void;
  inRiesecuzione?: boolean;
}

export function RisultatoBacktest({
  dato,
  periodo,
  onRiesegui,
  inRiesecuzione = false,
}: PropsRisultatoBacktest) {
  const serie = serieDaBacktest(dato);

  return (
    <RisultatoOperazioni
      serie={serie}
      quandoVuoto={
        <SerieAssenti
          dato={dato}
          periodo={periodo}
          onRiesegui={onRiesegui}
          inRiesecuzione={inRiesecuzione}
        />
      }
    />
  );
}

/* ==========================================================================
 * IL VUOTO CHE CONTA
 *
 * Le righe nate da una griglia — oltre un milione e mezzo, cioè quasi tutte —
 * non hanno `trades`, `equityLinePoints` né `candlesClosePrices`: il comando di
 * gruppo li scarta prima di salvare, perché sarebbero megabyte a riga. Quindi
 * la configurazione migliore trovata in una ricerca si apre senza curva del
 * capitale e senza operazioni, che sono proprio le due cose che si guardano
 * prima di metterci dei soldi. Questo riquadro è la parte più importante della
 * pagina quando compare: è da qui che si capisce che va rieseguito.
 * ======================================================================== */

function SerieAssenti({
  dato,
  periodo,
  onRiesegui,
  inRiesecuzione,
}: {
  dato: Backtest;
  periodo: string;
  onRiesegui?: () => void;
  inRiesecuzione: boolean;
}) {
  const daGriglia = dato.backtestGroupId != null;

  /* Mentre il motore calcola, le serie non mancano: non ci sono ANCORA.
     Offrire qui una riesecuzione significherebbe proporre di rifare una cosa
     che sta già succedendo, e ne creerebbe una terza copia. */
  if (dato.status === 'RUNNING') {
    return (
      <div className="sb-bt-riquadro sb-bt-serie-assenti">
        <div className="sb-bt-riquadro__testata">
          <span className="sb-bt-riquadro__titolo">Operazioni e grafici</span>
          <Distintivo tono="cautela">in calcolo</Distintivo>
        </div>
        {/* L'animazione ha la forma di quello che si sta aspettando: barre che si alzano e si
            abbassano, come il grafico che comparira' al posto suo. Un'attesa che somiglia al
            risultato si legge senza spiegazioni, e sotto `prefers-reduced-motion` si ferma
            restando comunque una figura sensata. */}
        <div className="sb-bt-calcolo" aria-hidden="true">
          {[0, 1, 2, 3, 4, 5, 6].map((i) => (
            <span key={i} className="sb-bt-calcolo__barra" style={{ animationDelay: `${i * 0.11}s` }} />
          ))}
        </div>
        <p className="sb-bt-serie-assenti__spiega">
          Il motore sta eseguendo questo backtest: operazioni, prezzi e curva del capitale
          compaiono qui quando ha finito. La pagina si aggiorna da sola ogni cinque secondi.
        </p>
      </div>
    );
  }

  return (
    <div className="sb-bt-riquadro sb-bt-serie-assenti">
      <div className="sb-bt-riquadro__testata">
        <span className="sb-bt-riquadro__titolo">Operazioni e grafici</span>
        <Distintivo tono="cautela">non salvati</Distintivo>
      </div>

      <p className="sb-bt-serie-assenti__spiega">
        {daGriglia
          ? `Questa riga viene dalla griglia #${dato.backtestGroupId}, e le griglie non salvano le operazioni: con migliaia di righe per gruppo, le serie di operazioni, prezzi e capitale peserebbero megabyte ciascuna. Restano le metriche, calcolate su tutto il periodo.`
          : 'Questa riga non porta con sé le serie di operazioni, prezzi e capitale: sono state scartate al salvataggio, oppure il calcolo si è interrotto prima di scriverle. Le metriche invece ci sono.'}
      </p>

      {dato.tradesNumber > 0 && (
        <p className="sb-bt-serie-assenti__spiega">
          Il motore ha contato <b>{num(dato.tradesNumber)}</b> operazioni su {periodo}: il numero è
          stato registrato, l'elenco no.
        </p>
      )}

      {onRiesegui && (
        <>
          <Avvertenza livello="cautela">
            Eseguire crea un <b>backtest nuovo</b>, non ricarica questo: parte con gli stessi
            parametri, impiega il tempo di un calcolo intero e finisce in una riga a sé. Se nel
            frattempo le candele si sono estese oltre {periodo}, i numeri possono differire di poco.
            {daGriglia &&
              ' La riga nuova sarà fuori dalla griglia, quindi non avrà intorno né tenuta: quei due numeri esistono solo dentro un gruppo.'}
          </Avvertenza>

          {/* I costi non sono salvati sulla riga: dirlo prima, non dopo. Un
              risultato misurato con costi diversi da quelli di partenza non è
              confrontabile con la riga da cui si è partiti. */}
          {dato.traderParams == null && (
            <Avvertenza livello="nota">
              I costi di esecuzione non sono registrati su questa riga: la riesecuzione usa gli
              stessi predefiniti con cui è stata misurata la griglia — nessuno slippage e le
              commissioni dell'exchange. Per provare costi diversi, usa il modulo del backtest
              libero.
            </Avvertenza>
          )}

          <Pulsante variante="primario" onClick={onRiesegui} disabled={inRiesecuzione}>
            {inRiesecuzione ? 'Avvio in corso…' : 'Esegui per vedere le operazioni'}
          </Pulsante>
        </>
      )}
    </div>
  );
}

/* --------------------------------------------------------------------------
 * Il profitto non compare mai da solo: chi mostra un risultato monta anche
 * questa terna. Sta qui e non dentro la pagina perché la regola vale per tutte
 * le schermate che mostrano l'esito di un backtest, non per una sola.
 * ------------------------------------------------------------------------ */

export function TernaDelRisultato({ dato, periodo }: { dato: Backtest; periodo: string }) {
  return (
    <TernaMetriche
      densita="riquadri"
      profitto={dato.strategyChange}
      drawdown={dato.drawdownMaxValue}
      operazioni={dato.tradesNumber}
      periodo={periodo}
    />
  );
}
